// ── Themed confirm dialog ───────────────────────────────────────────────────
// Replaces window.confirm() across the app -- the browser's native dialog
// (plain white, OS-styled) looked jarringly out of place against the dark,
// cyan-bordered theme every other modal in the product uses. Feedback
// 2026-11-02: "ihan eri teemaiselta kuin mitä sovelluksemme muuten on."
//
// Usage: `if (!(await confirmDialog('Delete this?'))) return;` anywhere in
// the app, as a drop-in async replacement for `if (!window.confirm(...))`.
// A single <ConfirmDialogHost /> renders the actual modal; it's mounted once
// near the app root (App.js) so every caller shares it instead of each
// needing its own open/close state.
import React, { useState, useEffect } from 'react';

const C = {
  bg: '#161b22',
  border: 'rgba(56,189,248,0.65)',
  dangerBorder: 'rgba(239,68,68,0.65)',
  text: '#e6edf3',
  muted: '#8b98a5',
  font: "'Inter', system-ui, sans-serif",
};

let pendingResolve = null;
let pushState = null;

export function confirmDialog(message, opts = {}) {
  return new Promise((resolve) => {
    if (!pushState) { resolve(window.confirm(message)); return; } // host not mounted yet — fail safe, never silently proceed
    pendingResolve = resolve;
    pushState({ message, ...opts });
  });
}

export function ConfirmDialogHost() {
  const [state, setState] = useState(null);
  useEffect(() => {
    pushState = setState;
    return () => { pushState = null; };
  }, []);

  if (!state) return null;

  const finish = (result) => {
    setState(null);
    const resolve = pendingResolve;
    pendingResolve = null;
    if (resolve) resolve(result);
  };

  const danger = state.danger !== false; // most confirms in this app are deletes — default to the red treatment

  return (
    <div onClick={() => finish(false)} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 4000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.bg, border: `1.5px solid ${danger ? C.dangerBorder : C.border}`,
        borderRadius: 16, padding: 22, width: '100%', maxWidth: 400, fontFamily: C.font,
      }}>
        {state.title && (
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{state.title}</div>
        )}
        <div style={{ color: C.text, fontSize: 13.5, lineHeight: 1.55, marginBottom: 22 }}>{state.message}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => finish(false)} autoFocus style={{
            background: 'none', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 8,
            color: C.muted, padding: '8px 16px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: C.font,
          }}>{state.cancelLabel || 'Cancel'}</button>
          <button type="button" onClick={() => finish(true)} style={{
            background: danger ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
            border: `1px solid ${danger ? '#ef4444' : '#10b981'}`, borderRadius: 8,
            color: danger ? '#fca5a5' : '#10b981', padding: '8px 16px', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: C.font,
          }}>{state.confirmLabel || (danger ? 'Delete' : 'OK')}</button>
        </div>
      </div>
    </div>
  );
}
